import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useI18n } from "../context/I18nContext";
import { useAuth, formatApiError, api } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Leaf, Gift } from "@phosphor-icons/react";
import { motion } from "framer-motion";

export default function Register() {
  const { t } = useI18n();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = (searchParams.get("ref") || "").trim().toUpperCase();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [refInput, setRefInput] = useState(refCode);
  const [refCheck, setRefCheck] = useState({ status: "idle", name: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const parseRef = (raw) => {
    const v = (raw || "").trim();
    if (!v) return "";
    const m = v.match(/[?&]ref=([^&\s]+)/i);
    return (m ? m[1] : v).trim().toUpperCase();
  };

  useEffect(() => {
    const code = parseRef(refInput);
    if (!code) { setRefCheck({ status: "idle", name: "" }); return; }
    setRefCheck({ status: "checking", name: "" });
    const timer = setTimeout(() => {
      api.get(`/referral/lookup/${encodeURIComponent(code)}`)
        .then((r) => setRefCheck(r.data.valid ? { status: "valid", name: r.data.name } : { status: "invalid", name: "" }))
        .catch(() => setRefCheck({ status: "invalid", name: "" }));
    }, 450);
    return () => clearTimeout(timer);
  }, [refInput]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email && !form.phone) {
      setError(t("email_or_phone"));
      return;
    }
    setLoading(true);
    try {
      const payload = { name: form.name, password: form.password };
      if (form.email) payload.email = form.email;
      if (form.phone) payload.phone = form.phone;
      const code = parseRef(refInput);
      if (code) payload.ref_code = code;
      const u = await register(payload);
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-emerald-900 grain text-white p-12">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/logo.png" className="h-11 w-11 rounded-lg bg-white p-1 object-contain" alt="logo" />
          <span className="font-display font-extrabold text-xl">{t("brand")}</span>
        </Link>
        <div>
          <Leaf size={48} weight="fill" className="text-emerald-400 mb-6" />
          <h2 className="font-display text-4xl font-bold leading-tight">{t("hero_title")}</h2>
          <p className="mt-4 text-emerald-200 max-w-md">{t("hero_sub")}</p>
        </div>
        <div className="text-emerald-400 text-sm">© {new Date().getFullYear()} {t("brand")}</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F8FAFC]">
        <motion.form
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          onSubmit={submit} className="w-full max-w-md" data-testid="register-form"
        >
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/logo.png" className="h-9 w-9" alt="logo" />
            <span className="font-display font-extrabold text-emerald-900 text-lg">{t("brand")}</span>
          </Link>
          <h1 className="font-display text-3xl font-bold text-emerald-900">{t("create_account")}</h1>
          <p className="text-slate-500 mt-2 mb-8">{t("register_sub")}</p>

          {error && <div data-testid="register-error" className="mb-4 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">{error}</div>}
          {refCode && (
            <div data-testid="register-ref-note" className="mb-4 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm px-4 py-3 flex items-center gap-2">
              <Gift size={18} weight="fill" /> {t("invited_note")} <span className="font-mono-num font-bold">({refCode})</span>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label className="text-emerald-900 font-semibold">{t("full_name")}</Label>
              <Input data-testid="register-name-input" className="mt-1.5 h-11" value={form.name} onChange={set("name")} required />
            </div>
            <div>
              <Label className="text-emerald-900 font-semibold">{t("email")} <span className="text-slate-400 font-normal">({t("optional")})</span></Label>
              <Input data-testid="register-email-input" type="email" className="mt-1.5 h-11" value={form.email} onChange={set("email")} />
            </div>
            <div>
              <Label className="text-emerald-900 font-semibold">{t("phone")} <span className="text-slate-400 font-normal">({t("optional")})</span></Label>
              <Input data-testid="register-phone-input" className="mt-1.5 h-11" value={form.phone} onChange={set("phone")} />
            </div>
            <div>
              <Label className="text-emerald-900 font-semibold">{t("password")}</Label>
              <Input data-testid="register-password-input" type="password" className="mt-1.5 h-11" value={form.password} onChange={set("password")} required />
            </div>
            <div>
              <Label className="text-emerald-900 font-semibold flex items-center gap-1.5">
                <Gift size={16} weight="fill" className="text-emerald-600" /> {t("referral_code_label")} <span className="text-slate-400 font-normal">({t("optional")})</span>
              </Label>
              <Input data-testid="register-ref-input" className="mt-1.5 h-11" placeholder={t("referral_code_ph")} value={refInput} onChange={(e) => setRefInput(e.target.value)} />
              {refCheck.status === "checking" && (
                <p className="mt-1.5 text-xs text-slate-400" data-testid="register-ref-checking">{t("referral_checking")}</p>
              )}
              {refCheck.status === "valid" && (
                <p className="mt-1.5 text-xs text-emerald-700 font-semibold" data-testid="register-ref-valid">✓ {t("referred_by")}: {refCheck.name}</p>
              )}
              {refCheck.status === "invalid" && (
                <p className="mt-1.5 text-xs text-red-500 font-semibold" data-testid="register-ref-invalid">{t("referral_invalid")}</p>
              )}
            </div>
          </div>

          <Button data-testid="register-submit-btn" disabled={loading} className="w-full mt-6 h-11 bg-emerald-800 hover:bg-emerald-900 text-base">
            {loading ? t("loading") : t("register")}
          </Button>

          <p className="text-center text-sm text-slate-500 mt-6">
            {t("have_account")}{" "}
            <Link to="/login" data-testid="go-login-link" className="text-emerald-700 font-semibold hover:underline">{t("login")}</Link>
          </p>
        </motion.form>
      </div>
    </div>
  );
}
